import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart3, FileText, TrendingUp, XCircle, CheckCircle, RefreshCw, Filter, ChevronUp, ChevronDown, Calendar, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import dayjs from 'dayjs';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };
const inputClass = "w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors";
const inputBorder = { border: '1px solid rgba(99, 102, 241, 0.15)' };
const labelClass = "block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5";

const TemplateAnalytics = ({ isAdmin = false }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    return { startDate: fmt(sevenDaysAgo), endDate: fmt(today) };
  });

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.startDate) params.start_date = dateRange.startDate;
      if (dateRange.endDate) params.end_date = dateRange.endDate;

      const endpoint = isAdmin ? '/admin/templates/analytics' : '/user/templates/analytics';
      const response = await api.get(endpoint, { params });
      
      if (response.data?.success) {
        setAnalyticsData(response.data.data);
      } else {
        throw new Error(response.data?.error || 'Failed to fetch analytics data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const activeFilterCount = [dateRange.startDate, dateRange.endDate].filter(v => v && v.trim() !== '').length;

  // Colors for charts
  const COLORS = ['#4f46e5', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            Template Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-1">Template usage statistics and performance insights</p>
        </div>
        <button
          onClick={() => fetchAnalyticsData()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
          style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Collapsible Filter Panel */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50 transition-colors cursor-pointer select-none"
          style={{ background: 'rgba(99, 102, 241, 0.02)' }}
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#6366f1' }} />
            <span className="text-sm font-semibold text-gray-700">Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white rounded-full" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                {activeFilterCount}
              </span>
            )}
          </div>
          {filtersOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {filtersOpen && (
          <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4">
              <div className="min-w-0">
                <label className={labelClass}>From Date</label>
                <input type="date" className={inputClass} style={inputBorder} value={dateRange.startDate} onChange={(e) => handleDateChange('startDate', e.target.value)} />
              </div>
              <div className="min-w-0">
                <label className={labelClass}>To Date</label>
                <input type="date" className={inputClass} style={inputBorder} value={dateRange.endDate} onChange={(e) => handleDateChange('endDate', e.target.value)} />
              </div>
              <div className="min-w-0 flex items-end">
                <button
                  onClick={() => setDateRange({ startDate: '', endDate: '' })}
                  className="h-9 px-4 rounded-lg text-xs font-medium transition-all hover:shadow-sm"
                  style={{ color: '#6b7280', background: 'rgba(107, 114, 128, 0.06)', border: '1px solid rgba(107, 114, 128, 0.15)' }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="bg-white rounded-xl p-16 flex flex-col items-center" style={cardStyle}>
          <RefreshCw className="w-8 h-8 animate-spin mb-3" style={{ color: '#6366f1' }} />
          <p className="text-sm text-gray-500">Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                <FileText className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">{analyticsData?.total_templates?.toLocaleString() || 0}</div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total Templates</div>
              </div>
            </div>
            <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #059669, #34d399)' }}>
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">{analyticsData?.usage_stats?.total_sms_sent?.toLocaleString() || 0}</div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total SMS Sent</div>
              </div>
            </div>
            <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(6, 182, 212, 0.06)', border: '1px solid rgba(6, 182, 212, 0.12)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0891b2, #06b6d4)' }}>
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">{analyticsData?.usage_stats?.success_rate?.toFixed(1) || 0}%</div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Success Rate</div>
              </div>
            </div>
            <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.12)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
                <XCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">{analyticsData?.usage_stats?.failure_count?.toLocaleString() || 0}</div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Failed SMS</div>
              </div>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl p-5" style={cardStyle}>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Daily Usage Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={analyticsData?.daily_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(99, 102, 241, 0.08)" />
                  <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format('DD-MM-YYYY')} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.15)', fontSize: '12px' }} labelFormatter={(d) => dayjs(d).format('DD-MM-YYYY')} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Line type="monotone" dataKey="total_sent" stroke="#4f46e5" strokeWidth={2} name="Total Sent" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="success_sent" stroke="#10b981" strokeWidth={2} name="Success" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl p-5" style={cardStyle}>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Template Type Distribution</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={analyticsData?.type_distribution || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {(analyticsData?.type_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.15)', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top Performing Templates */}
            <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
              <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <BarChart3 className="w-4 h-4" style={{ color: '#6366f1' }} />
                <span className="text-sm font-semibold text-gray-700">Top Performing Templates</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Template Name</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Entity</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Usage</th>
                      <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analyticsData?.top_templates || []).length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center">
                          <p className="text-sm text-gray-400 font-medium">No template data available</p>
                        </td>
                      </tr>
                    ) : (
                      (analyticsData?.top_templates || []).map((template, index) => (
                        <tr key={index} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                          <td className="px-4 py-3 text-xs font-medium text-gray-800">{template.template_name}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{template.entity_name}</td>
                          <td className="px-4 py-3 text-xs font-semibold text-gray-800">{template.usage_count?.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={
                              template.success_rate >= 95
                                ? { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' }
                                : template.success_rate >= 85
                                ? { background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)' }
                                : { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' }
                            }>
                              {template.success_rate?.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Entity-wise Statistics (Admin Only) */}
            {isAdmin && analyticsData?.entity_stats && (
              <div className="bg-white rounded-xl p-5" style={cardStyle}>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Entity-wise Statistics</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analyticsData.entity_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(99, 102, 241, 0.08)" />
                    <XAxis dataKey="entity_name" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.15)', fontSize: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="template_count" fill="#4f46e5" name="Templates" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="sms_sent" fill="#06b6d4" name="SMS Sent" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateAnalytics;
